import { AvatarCompanyData } from "@/components/company/avatar-company";
import { useTranslation } from "@/hooks/useTranslation";
import { ConversationMessagePromptInfo } from "@shaarp/common";
import { ReactNode, useRef } from "react";
import { ChatCompanyHomeHeader } from "./chat-company-home-header";
import { ChatCompanyTactics } from "./chat-company-tactics";

/**
 * Entry screen of a company's chat, before its first message (#707), laid out
 * like the chat's own welcome screen: a heading naming the company with a
 * commercial question, the composer, then the tactics pinned on company pages,
 * as a column of the chat's tactic cards. No suggestions: the composer keeps
 * its plain placeholder.
 *
 * The whole block sits just above the middle of the chat and keeps its own
 * height, so in a short panel it scrolls rather than squeezing the column.
 */
export function ChatCompanyHome({
  company,
  renderComposer,
  onPromptSelect,
  disabled = false,
}: {
  company: AvatarCompanyData;
  /** The chat's composer, owned by the caller with its send path. */
  renderComposer: () => ReactNode;
  onPromptSelect: (
    content: string,
    prompt: ConversationMessagePromptInfo,
  ) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  // As the company page shows it; a page with no name still reads as a sentence.
  const name = company.name?.trim() || t("chat.companyHome.title.fallbackName");

  // One send per screen: a second click while the first one is creating the
  // conversation would open a second conversation. The screen unmounts once the
  // send goes through, and comes back fresh if it fails.
  const hasSentRef = useRef(false);
  const handlePromptSelect = (
    content: string,
    prompt: ConversationMessagePromptInfo,
  ) => {
    if (hasSentRef.current) return;
    hasSentRef.current = true;
    onPromptSelect(content, prompt);
  };

  return (
    <div className="flex w-full h-full flex-col overflow-y-auto">
      {/* Spacers 2:3 put the content just above the middle of the chat; they
          shrink to nothing when the content is taller than the chat. */}
      <div aria-hidden className="flex-[2]" />
      <div className="mx-auto flex w-full max-w-[800px] shrink-0 flex-col gap-5 px-4 py-6">
        <ChatCompanyHomeHeader company={company} name={name} />
        {renderComposer()}
        <ChatCompanyTactics onSelect={handlePromptSelect} disabled={disabled} />
      </div>
      <div aria-hidden className="flex-[3]" />
    </div>
  );
}
